import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  Calendar,
  PieChart,
  MessageSquare,
  Shield,
  Download,
  Clock,
  TrendingUp,
  Users,
  CheckCircle,
  X,
  Check,
  ChevronDown,
  Bell,
} from "react-feather";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import PageHeader from "../../../Components/shared/PageHeader";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./ReportsHub.module.scss";

// ─── Brand palette (all purple shades) ───────────────────────────────────────
const P = "#644998";
const P_80 = "#7d62aa";
const P_60 = "#9880bc";
const P_40 = "#b39fcd";
const P_20 = "#cebfde";
const P_10 = "#ede8f5";
const COLORS = [P, P_80, P_60, P_40, P_20];

// ─── Dummy Data ───────────────────────────────────────────────────────────────
const DUMMY = {
  kpis: [
    {
      label: "Total Meetings",
      value: "1,284",
      delta: "+12%",
      icon: Calendar,
      up: true,
    },
    {
      label: "Active Users",
      value: "348",
      delta: "+5%",
      icon: Users,
      up: true,
    },
    {
      label: "Avg Duration (min)",
      value: "42",
      delta: "-3%",
      icon: Clock,
      up: false,
    },
    {
      label: "Completion Rate",
      value: "91%",
      delta: "+2%",
      icon: CheckCircle,
      up: true,
    },
  ],
  meetingsPerMonth: [
    { month: "Nov", meetings: 180, cancelled: 22 },
    { month: "Dec", meetings: 210, cancelled: 18 },
    { month: "Jan", meetings: 195, cancelled: 30 },
    { month: "Feb", meetings: 240, cancelled: 15 },
    { month: "Mar", meetings: 275, cancelled: 20 },
    { month: "Apr", meetings: 184, cancelled: 12 },
  ],
  meetingsByType: [
    { name: "Scheduled", value: 620 },
    { name: "Ad-hoc", value: 310 },
    { name: "Recurring", value: 218 },
    { name: "External", value: 136 },
  ],
  userActivity: [
    { week: "W1", active: 280, invited: 40 },
    { week: "W2", active: 305, invited: 55 },
    { week: "W3", active: 290, invited: 48 },
    { week: "W4", active: 320, invited: 62 },
    { week: "W5", active: 348, invited: 70 },
  ],
  categories: [
    {
      id: "meetings",
      icon: Calendar,
      title: "Meeting Analytics",
      description: "Volume, duration, cancellation rates and trends over time.",
      path: "reports/meetings",
    },
    {
      id: "users",
      icon: Users,
      title: "User Activity",
      description: "Logins, engagement levels and per-user meeting stats.",
      path: "reports/users",
    },

    {
      id: "audit",
      icon: Shield,
      title: "Audit & Compliance",
      description: "Role changes, login anomalies, permission matrix exports.",
      path: "reports/audit",
    },
  ],
  savedReports: [
    {
      id: 1,
      name: "Q1 2025 Meeting Summary",
      lastRunAt: "2025-04-01T09:00:00Z",
      runBy: "Admin",
    },
    {
      id: 2,
      name: "User Engagement — March",
      lastRunAt: "2025-03-31T14:22:00Z",
      runBy: "HR Manager",
    },
  ],
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} className={styles.tooltipRow}>
          <span
            className={styles.tooltipDot}
            style={{ background: p.color || P }}
          />
          <span>
            {p.name}: <strong>{p.value}</strong>
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Select Dropdown ──────────────────────────────────────────────────────────
function SelectDropdown({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sel = options.find((o) => o.value === value);
  return (
    <div className={styles.selectWrap} ref={ref}>
      <button
        className={`${styles.selectTrigger} ${open ? styles.open : ""}`}
        onClick={() => setOpen(!open)}
      >
        <span>{sel?.label}</span>
        <ChevronDown
          size={13}
          className={`${styles.chevron} ${open ? styles.open : ""}`}
        />
      </button>
      {open && (
        <div className={styles.selectDropdown}>
          {options.map((o) => (
            <div
              key={o.value}
              className={`${styles.selectOption} ${o.value === value ? styles.selected : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span className={styles.selectCheck}>
                {o.value === value && <Check size={11} />}
              </span>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Safe Chart Wrapper ───────────────────────────────────────────────────────
function SafeChart({ children, height = 220 }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div style={{ height }} className={styles.chartPlaceholder} />;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
}

export default function ReportsHub() {
  const navigate = useNavigate();
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedReport, setSchedReport] = useState(null);
  const [sched, setSched] = useState({ frequency: "weekly", email: "" });
  const [chartKey, setChartKey] = useState(0);

  // Force re-render of charts after mount
  useEffect(() => {
    const timer = setTimeout(() => setChartKey((prev) => prev + 1), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Reports Hub"
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Reports" },
        ]}
      />

      {/* ── KPI Row ── */}
      <div className={styles.kpiRow}>
        {DUMMY.kpis.map(({ label, value, delta, icon: Icon, up }) => (
          <div key={label} className={styles.kpiCard}>
            <div className={styles.kpiIcon}>
              <Icon size={15} />
            </div>
            <div className={styles.kpiValue}>{value}</div>
            <div className={styles.kpiLabel}>{label}</div>
            <div
              className={`${styles.kpiDelta} ${up ? styles.up : styles.down}`}
            >
              {delta} vs last period
            </div>
          </div>
        ))}
      </div>

      {/* ── Charts Row ── */}
      <div className={styles.chartsRow}>
        {/* Bar chart — meetings per month */}
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>Meetings per Month</div>
          <SafeChart height={220}>
            <BarChart
              key={`bar-${chartKey}`}
              data={DUMMY.meetingsPerMonth}
              barSize={18}
              barGap={4}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(100,73,152,0.1)"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#8a8a9a" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#8a8a9a" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="meetings"
                name="Meetings"
                fill={P}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="cancelled"
                name="Cancelled"
                fill={P_40}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </SafeChart>
        </div>

        {/* Pie chart — meeting type */}
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>Meetings by Type</div>
          <SafeChart height={220}>
            <RePieChart key={`pie-${chartKey}`}>
              <Pie
                data={DUMMY.meetingsByType}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
                isAnimationActive={false}
              >
                {DUMMY.meetingsByType.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, color: "#8a8a9a" }}
              />
            </RePieChart>
          </SafeChart>
        </div>

        {/* Line chart — user activity */}
        <div className={styles.chartCard}>
          <div className={styles.chartTitle}>User Activity (Last 5 Weeks)</div>
          <SafeChart height={220}>
            <LineChart key={`line-${chartKey}`} data={DUMMY.userActivity}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(100,73,152,0.1)"
                vertical={false}
              />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11, fill: "#8a8a9a" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#8a8a9a" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="active"
                name="Active"
                stroke={P}
                strokeWidth={2.5}
                dot={{ r: 4, fill: P }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="invited"
                name="Invited"
                stroke={P_40}
                strokeWidth={2}
                dot={{ r: 3, fill: P_40 }}
                strokeDasharray="5 3"
                isAnimationActive={false}
              />
            </LineChart>
          </SafeChart>
        </div>
      </div>

      {/* ── Report Categories ── */}
      {/* <div className={styles.sectionHeader}>Report Categories</div>
      <div className={styles.categoryGrid}>
        {DUMMY.categories.map(
          ({ id, icon: Icon, title, description, path }) => (
            <div key={id} className={styles.categoryCard}>
              <div className={styles.categoryIcon}>
                <Icon size={18} />
              </div>
              <div className={styles.categoryBody}>
                <div className={styles.categoryTitle}>{title}</div>
                <div className={styles.categoryDesc}>{description}</div>
              </div>
              <button
                className={styles.runBtn}
                onClick={() => navigate(companyUrl(path))}
              >
                Run Report
              </button>
            </div>
          ),
        )}
      </div> */}

      {/* ── Saved Reports ── */}
      <div className={styles.sectionHeader}>Saved Reports</div>
      <div className={styles.savedTable}>
        <div className={styles.savedHeader}>
          <span>Report Name</span>
          <span>Last Run</span>
          <span>Run By</span>
          <span>Actions</span>
        </div>
        {DUMMY.savedReports.map((s) => (
          <div key={s.id} className={styles.savedRow}>
            <span className={styles.savedName}>{s.name}</span>
            <span className={styles.savedMeta}>
              {new Date(s.lastRunAt).toLocaleDateString()}
            </span>
            <span className={styles.savedMeta}>{s.runBy}</span>
            <div className={styles.savedActions}>
              <button
                className={styles.actionBtn}
                onClick={() => toast.success("Downloading…")}
              >
                <Download size={13} /> Download
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
